import React, { useState, useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet-async";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ErrorBoundary from "@/components/ErrorBoundary";

// Heavy UI widgets — lazy loaded after initial render
const ChatWidget      = lazy(() => import("@/components/ChatWidget"));
const MobileBottomNav = lazy(() => import("@/components/MobileBottomNav"));
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import { toast } from "@/hooks/use-toast";
import { publicAPI } from "@/services/api";

// Only Home is eager (it's the LCP page — everything else is lazy)
import Home from "@/pages/Home";

// Auth & misc pages — lazy loaded
const Login    = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Lazy-loaded pages (loaded only when user navigates to them)
const ForgotPassword    = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword     = lazy(() => import("@/pages/ResetPassword"));
const VerifyOTP         = lazy(() => import("@/pages/VerifyOTP"));
const Dashboard         = lazy(() => import("@/pages/Dashboard"));
const Profile           = lazy(() => import("@/pages/Profile"));
const WalletPage        = lazy(() => import("@/pages/WalletPage"));
const CreateTransaction = lazy(() => import("@/pages/CreateTransaction"));
const TransactionDetail = lazy(() => import("@/pages/TransactionDetail"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const AdminPanel        = lazy(() => import("@/pages/AdminPanel"));
const AdminChatDetail   = lazy(() => import("@/pages/AdminChatDetail"));
const TermsOfService    = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy     = lazy(() => import("@/pages/PrivacyPolicy"));
const HowItWorks        = lazy(() => import("@/pages/HowItWorks"));
const FAQ               = lazy(() => import("@/pages/FAQ"));
const Contact           = lazy(() => import("@/pages/Contact"));
const About             = lazy(() => import("@/pages/About"));
const Categories        = lazy(() => import("@/pages/Categories"));
const Blog              = lazy(() => import("@/pages/Blog"));
const BlogPost          = lazy(() => import("@/pages/BlogPost"));
const SEOManager              = lazy(() => import("@/pages/SEOManager"));

const PageSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
  </div>
);

/** Remove nginx/backend SEO crawl blocks once React mounts (avoid flash / duplicate UI). */
function SeoPrerenderCleanup() {
  useEffect(() => {
    document.getElementById("seo-prerender")?.remove();
    document.getElementById("seo-crawl-nav")?.remove();
  }, []);
  return null;
}

/* Route guards — defined OUTSIDE App() to prevent remount on every render */
function ProtectedRoute({ children, adminOnly = false, isAuthenticated, user }) {
  const location = useLocation();
  if (!isAuthenticated) {
    const nextPath = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
  }
  if (adminOnly && !['admin', 'super_admin'].includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      {children}
    </>
  );
}

function PublicOnlyRoute({ children, isAuthenticated, user }) {
  if (isAuthenticated) {
    const dest = ['admin', 'super_admin'].includes(user?.role) ? '/admin' : '/dashboard';
    return <Navigate to={dest} replace />;
  }
  return (
    <>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      {children}
    </>
  );
}

/* Scroll-to-top on every route change */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

/* Inner shell — uses useLocation() which requires being inside BrowserRouter */
function AppShell({ children, user, isAuthenticated, onLogout }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin') || pathname.startsWith('/seomanager');

  // Defer heavy floating widgets until after first paint / LCP
  const [widgetsReady, setWidgetsReady] = useState(false);
  useEffect(() => {
    // Use requestIdleCallback when available, otherwise setTimeout
    const schedule = window.requestIdleCallback
      ? (cb) => window.requestIdleCallback(cb, { timeout: 3000 })
      : (cb) => setTimeout(cb, 800);
    const id = schedule(() => setWidgetsReady(true));
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  // Public visit counter — once per browser session (marketing pages only)
  useEffect(() => {
    if (isAdmin) return;
    try {
      if (sessionStorage.getItem('tg247_visit_tracked')) return;
      sessionStorage.setItem('tg247_visit_tracked', '1');
    } catch { /* ignore */ }
    publicAPI.trackVisit();
  }, [isAdmin]);

  return (
    <>
      {!isAdmin
        ? <Navbar isAuthenticated={isAuthenticated} user={user} onLogout={onLogout} />
        : <div className="lg:hidden"><Navbar isAuthenticated={isAuthenticated} user={user} onLogout={onLogout} /></div>
      }
      <main>{children}</main>
      {!isAdmin && <Footer />}
      {!isAdmin && widgetsReady && (
        <Suspense fallback={null}>
          <ChatWidget user={user} />
          <MobileBottomNav user={user} isAuthenticated={isAuthenticated} />
        </Suspense>
      )}
    </>
  );
}

function App() {
  // Read auth state synchronously to avoid an initial blank-spinner render cycle
  const [user, setUser] = useState(() => {
    try {
      // Migrate old sessionStorage keys → new localStorage keys (one-time migration)
      const oldToken = sessionStorage.getItem('escrowvn_token');
      const oldUser  = sessionStorage.getItem('escrowvn_user');
      if (oldToken && oldUser && !localStorage.getItem('tg247_token')) {
        localStorage.setItem('tg247_token', oldToken);
        localStorage.setItem('tg247_user', oldUser);
        sessionStorage.removeItem('escrowvn_token');
        sessionStorage.removeItem('escrowvn_user');
      }
      const u = localStorage.getItem('tg247_user');
      const t = localStorage.getItem('tg247_token');
      if (u && t) return JSON.parse(u);
    } catch {
      localStorage.removeItem('tg247_user');
      localStorage.removeItem('tg247_token');
    }
    return null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!(localStorage.getItem('tg247_user') && localStorage.getItem('tg247_token'))
  );

  // Global 429 rate-limit toast — catches events dispatched by api.js interceptor
  useEffect(() => {
    const handler = (e) => {
      const { message, retryAfter } = e.detail || {};
      toast({
        title: '⏳ Thao tác quá nhanh',
        description: message || `Vui lòng thử lại sau ${retryAfter || 60} giây.`,
        variant: 'warning',
        duration: Math.min((retryAfter || 10) * 1000, 10000),
      });
    };
    window.addEventListener('api:rateLimit', handler);
    return () => window.removeEventListener('api:rateLimit', handler);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('tg247_user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('tg247_user');
    localStorage.removeItem('tg247_token');
    sessionStorage.removeItem('tg247_new_user_redirected');
  };

  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <ErrorBoundary>
        <WebSocketProvider user={user} isAuthenticated={isAuthenticated}>
        <AppShell user={user} isAuthenticated={isAuthenticated} onLogout={handleLogout}>
        <ScrollToTop />
        <SeoPrerenderCleanup />
        <Suspense fallback={<PageSpinner />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<PublicOnlyRoute isAuthenticated={isAuthenticated} user={user}><Login onLogin={handleLogin} /></PublicOnlyRoute>} />
          <Route path="/register" element={<PublicOnlyRoute isAuthenticated={isAuthenticated} user={user}><Register /></PublicOnlyRoute>} />
          <Route path="/forgot-password" element={<PublicOnlyRoute isAuthenticated={isAuthenticated} user={user}><ForgotPassword /></PublicOnlyRoute>} />
          <Route path="/reset-password" element={<PublicOnlyRoute isAuthenticated={isAuthenticated} user={user}><ResetPassword /></PublicOnlyRoute>} />
          <Route path="/verify-otp" element={<VerifyOTP onLogin={handleLogin} />} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute isAuthenticated={isAuthenticated} user={user}><Dashboard user={user} /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute isAuthenticated={isAuthenticated} user={user}><Profile user={user} onUserUpdate={setUser} /></ProtectedRoute>} />
          <Route path="/wallet" element={<ProtectedRoute isAuthenticated={isAuthenticated} user={user}><WalletPage /></ProtectedRoute>} />
          <Route path="/create-transaction" element={<ProtectedRoute isAuthenticated={isAuthenticated} user={user}><CreateTransaction /></ProtectedRoute>} />
          <Route path="/transaction/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} user={user}><TransactionDetail /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute isAuthenticated={isAuthenticated} user={user}><NotificationsPage /></ProtectedRoute>} />

          {/* Admin Only Routes */}
          <Route path="/admin" element={<ProtectedRoute adminOnly={true} isAuthenticated={isAuthenticated} user={user}><AdminPanel /></ProtectedRoute>} />
          <Route path="/admin/chat/:sessionId" element={<ProtectedRoute adminOnly={true} isAuthenticated={isAuthenticated} user={user}><AdminChatDetail /></ProtectedRoute>} />
          {/* Static info pages */}
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/gioi-thieu" element={<About />} />
          <Route path="/about" element={<Navigate to="/gioi-thieu" replace />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* SEO Manager — Mini CMS (admin/super_admin only, handled internally) */}
          <Route path="/seomanager" element={<ProtectedRoute adminOnly={true} isAuthenticated={isAuthenticated} user={user}><SEOManager /></ProtectedRoute>} />
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </AppShell>
        </WebSocketProvider>
        <Suspense fallback={null}><Toaster /></Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
